import { store } from "@/internal";
import Vue from "vue";
import Router from "vue-router";
import { policyList } from "./enums/permissions";
import RouterUtils from "./router.utils";

Vue.use(Router);

const routerUtils = new RouterUtils();

export const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  routes: [
    {
      path: "/",
      name: "home",
      component: () =>
        import(/* webpackChunkName: "home" */ "@/components/Home/Home.component"),
      beforeEnter: (to, from, next) => {
        const isLogged = store.getters["authModule/isLogged"];
        isLogged ? next("/dashboard") : next();
      }
    },
    {
      path: "/oauth/callback",
      name: "oauth-callback",
      component: () =>
        import(/* webpackChunkName: "login" */ "@/components/Login/Callback.component"),
      beforeEnter: routerUtils.noAuthenticated
    },
    {
      path: "/dashboard",
      name: "dashboard",
      component: () =>
        import(/* webpackChunkName: "dashboard" */ "@/components/Dashboard/Dashboard.component")
    },
    {
      path: "/application-management",
      component: () => import("@/components/Settings/Settings.component"),
      children: [
        {
          path: "",
          name: "settings-main-menu",
          component: () =>
            import("@/components/Settings/SettingsMainMenu/SettingsMainMenu.component")
        },
        {
          path: "locations",
          component: {
            render(c) {
              return c("router-view");
            }
          },
          children: [
            {
              path: "",
              name: "location-management-list",
              component: () =>
                import("@/components/Settings/LocationsManagement/LocationManagementList/LocationList.component"),
              beforeEnter: (to, from, next) => {
                routerUtils.isAllowedGuard(
                  policyList.viewLocations,
                  to,
                  from,
                  next
                );
              }
            },
            {
              path: "add",
              name: "add-location",
              component: () =>
                import(/* webpackChunkName: "location-manager" */ "@/components/Settings/LocationsManagement/LocationDetails/LocationDetails.component"),
              beforeEnter: (to, from, next) => {
                routerUtils.isAllowedGuard(
                  policyList.createLocations,
                  to,
                  from,
                  next
                );
              }
            },
            {
              path: `:id(${routerUtils.idRegex})/edit`,
              name: "edit-location",
              component: () =>
                import(/* webpackChunkName: "location-manager" */ "@/components/Settings/LocationsManagement/LocationDetails/LocationDetails.component"),
              beforeEnter: (to, from, next) => {
                routerUtils.isAllowedGuard(
                  policyList.modifyLocations,
                  to,
                  from,
                  next
                );
              }
            },
            {
              path: `:id(${routerUtils.idRegex})/view`,
              name: "view-location",
              component: () =>
                import(/* webpackChunkName: "location-manager" */ "@/components/Settings/LocationsManagement/LocationDetails/LocationDetails.component"),
              beforeEnter: (to, from, next) => {
                routerUtils.isAllowedGuard(
                  policyList.viewLocations,
                  to,
                  from,
                  next
                );
              }
            }
          ]
        },
        {
          path: "permissions",
          component: () =>
            import("@/components/Settings/PermissionManagement/PermissionManagement.component"),
          children: [
            {
              path: "",
              name: "permission-management-list",
              component: () =>
                import("@/components/Settings/PermissionManagement/PermissionManagementList/PermissionManagementList.component"),
              beforeEnter: (to, from, next) => {
                routerUtils.isAllowedGuard(
                  policyList.viewPermissionProfiles,
                  to,
                  from,
                  next
                );
              }
            },
            {
              path: "add",
              name: "permission-management-add",
              component: () =>
                import(/* webpackChunkName: "permission-roles" */ "@/components/Settings/PermissionManagement/Roles/roles.component"),
              beforeEnter: (to, from, next) => {
                routerUtils.isAllowedGuard(
                  policyList.createPermissionProfile,
                  to,
                  from,
                  next
                );
              }
            },
            {
              path: `:id(${routerUtils.idRegex})`,
              redirect: `:id(${routerUtils.idRegex})/edit`
            },
            {
              path: `:id(${routerUtils.idRegex})/edit`,
              name: "permission-management-edit",
              component: () =>
                import(/* webpackChunkName: "permission-roles" */ "@/components/Settings/PermissionManagement/Roles/roles.component"),
              beforeEnter: (to, from, next) => {
                routerUtils.isAllowedGuard(
                  policyList.modifyPermissionProfile,
                  to,
                  from,
                  next
                );
              }
            }
          ]
        },
        {
          path: "users",
          component: {
            render(c) {
              return c("router-view");
            }
          },
          children: [
            {
              path: "",
              name: "user-management-list",
              component: () =>
                import("@/components/Settings/UserManagement/UserManagementList/UserManagementList.component"),
              beforeEnter: (to, from, next) => {
                routerUtils.isAllowedGuard(
                  policyList.viewUsers,
                  to,
                  from,
                  next
                );
              }
            },
            {
              path: "add",
              name: "user-management-add",
              component: () =>
                import("@/components/Settings/UserManagement/UserManagementForm/UserManagementForm.component"),
              beforeEnter: (to, from, next) => {
                routerUtils.isAllowedGuard(
                  policyList.createUsers,
                  to,
                  from,
                  next
                );
              }
            },
            {
              path: `:id(${routerUtils.idRegex})`,
              redirect: `:id(${routerUtils.idRegex})/edit`
            },
            {
              path: `:id(${routerUtils.idRegex})/edit`,
              name: "user-management-edit",
              component: () =>
                import("@/components/Settings/UserManagement/UserManagementForm/UserManagementForm.component"),
              beforeEnter: (to, from, next) => {
                routerUtils.isAllowedGuard(
                  policyList.modifyUsers,
                  to,
                  from,
                  next
                );
              }
            },
            {
              path: `:id(${routerUtils.idRegex})/view`,
              name: "user-management-view",
              component: () =>
                import("@/components/Settings/UserManagement/UserManagementForm/UserManagementForm.component"),
              beforeEnter: (to, from, next) => {
                routerUtils.isAllowedGuard(
                  policyList.viewUsers,
                  to,
                  from,
                  next
                );
              }
            }
          ]
        },
        {
          path: "profile",
          name: "profile-management",
          component: () =>
            import("@/components/Settings/ProfileManagement/ProfileManagementForm/ProfileManagementForm.component"),
          props: true
        },
        {
          path: "location-api",
          component: {
            render(c) {
              return c("router-view");
            }
          },
          beforeEnter: (to, from, next) => {
            routerUtils.isAllowedGuard(
              policyList.viewApiManagement,
              to,
              from,
              next
            );
          },
          children: [
            {
              path: "",
              name: "location-api-management",
              component: () =>
                import(/*webpackChunkName: "location-api-management" */ "@/components/LocationApiManagement/LocationApiManagement.component")
            },
            {
              path: "client-integration/:id",
              redirect: "client-integration/:id/edit"
            },
            {
              path: "client-integration/:id/edit",
              name: "client-integration-edit",
              beforeEnter: (to, from, next) => {
                routerUtils.isAllowedGuard(
                  policyList.createApiToken,
                  to,
                  from,
                  next
                );
              },
              component: () =>
                import(/*webpackChunkName: "client-integration-edit"*/ "@/components/LocationApiManagement/ThirdParty/edit/EditClient.component")
            },
            {
              path: "weedmaps-integration",
              name: "weedmaps",
              beforeEnter: (to, from, next) => {
                routerUtils.isAllowedGuard(
                  policyList.createApiToken,
                  to,
                  from,
                  next
                );
              },
              component: () =>
                import(/*webpackChunkName: "client-integration-edit"*/ "@/components/LocationApiManagement/LocationApiManagement.component")
            },
            {
              path: "quickbooks-integration",
              name: "quickbooks",
              beforeEnter: (to, from, next) => {
                routerUtils.isAllowedGuard(
                  policyList.createApiToken,
                  to,
                  from,
                  next
                );
              },
              component: () =>
                import(/*webpackChunkName: "client-integration-edit"*/ "@/components/LocationApiManagement/LocationApiManagement.component")
            },
            {
              path: "stems-key-integration",
              name: "stems-key",
              beforeEnter: (to, from, next) => {
                routerUtils.isAllowedGuard(
                  policyList.createApiToken,
                  to,
                  from,
                  next
                );
              },
              component: () =>
                import(/*webpackChunkName: "client-integration-edit"*/ "@/components/LocationApiManagement/LocationApiManagement.component")
            },
            {
              path: "third-party-integration",
              name: "third-party",
              beforeEnter: (to, from, next) => {
                routerUtils.isAllowedGuard(
                  policyList.createApiToken,
                  to,
                  from,
                  next
                );
              },
              component: () =>
                import(/*webpackChunkName: "client-integration-edit"*/ "@/components/LocationApiManagement/LocationApiManagement.component")
            },
            {
              path: "pmp-integration",
              name: "pmp-integration",
              beforeEnter: (to, from, next) => {
                routerUtils.isAllowedGuard(
                  policyList.viewPmpIntegration,
                  to,
                  from,
                  next
                );
              },
              component: () =>
                import(/*webpackChunkName: "client-integration-edit"*/ "@/components/LocationApiManagement/LocationApiManagement.component")
            },
            {
              path: `pmp-integration/:id(${routerUtils.idRegex})/edit`,
              name: "pmp-edit",
              component: () =>
                import(/* webpackChunkName: "pmp-integration-edit" */ "@/components/LocationApiManagement/Pmp/pmpIntegrationEdit/PmpIntegrationEdit.component")
            },
            {
              path: "state-traceability-integration",
              name: "state-traceability",
              component: () =>
                import(/*webpackChunkName: "client-integration-edit"*/ "@/components/LocationApiManagement/LocationApiManagement.component")
            },
            {
              path: `state-traceability-integration/:id(${
                routerUtils.idRegex
              })`,
              redirect: `state-traceability-integration/:id(${
                routerUtils.idRegex
              })/edit`
            },
            {
              path: `state-traceability-integration/:id(${
                routerUtils.idRegex
              })/edit`,
              name: "trace-integration-edit",
              component: () =>
                import(/* webpackChunkName: "trace-edit" */ "@/components/LocationApiManagement/StateTraceability/IntegrationEdit/IntegrationEdit.component"),
              beforeEnter: (to, from, next) => {
                if (+process.env.VUE_APP_METRC) {
                  next();
                } else {
                  next(from || { name: "home" });
                }
              }
            },
            {
              path: `state-traceability-integration/:id(${
                routerUtils.idRegex
              })/view`,
              name: "trace-integration-view",
              component: () =>
                import(/* webpackChunkName: "trace-edit" */ "@/components/LocationApiManagement/StateTraceability/IntegrationEdit/IntegrationEdit.component"),
              beforeEnter: (to, from, next) => {
                if (+process.env.VUE_APP_METRC) {
                  next();
                } else {
                  next(from || { name: "home" });
                }
              }
            }
          ]
        }
      ]
    },
    {
      path: "/account",
      component: {
        render(c) {
          return c("router-view");
        }
      },
      children: [
        {
          path: "",
          component: () =>
            import(/* webpackChunkName: "account" */ "@/components/AccountManagement/AccountManagement.component"),
          beforeEnter: (to, from, next) => {
            routerUtils.isAllowedGuard(
              policyList.viewAccountManagement,
              to,
              from,
              next
            );
          }
        },
        {
          path: "credit-cards",
          name: "credit-cards",
          component: () =>
            import(/* webpackChunkName: "account" */ "@/components/AccountManagement/AccountCreditCards/AccountCreditCards.component"),
          beforeEnter: (to, from, next) => {
            routerUtils.isAllowedGuard(
              policyList.viewCreditCards,
              to,
              from,
              next
            );
          }
        },
        {
          path: "credit-cards/new",
          name: "new-credit-card",
          component: () =>
            import(/* webpackChunkName: "account" */ "@/components/AccountManagement/AccountCreditCards/CreditCardForm/CreditCardForm.component"),
          beforeEnter: (to, from, next) => {
            routerUtils.isAllowedGuard(
              policyList.createCreditCards,
              to,
              from,
              next
            );
          }
        },
        {
          path: `credit-cards/:id(${routerUtils.idRegex})`,
          redirect: `credit-cards/:id(${routerUtils.idRegex})/edit`
        },
        {
          path: `credit-cards/:id(${routerUtils.idRegex})/edit`,
          name: "edit-credit-card",
          component: () =>
            import(/* webpackChunkName: "account" */ "@/components/AccountManagement/AccountCreditCards/CreditCardForm/CreditCardForm.component"),
          props: route => {
            return {
              card:
                typeof route.params.card === "string"
                  ? JSON.parse(route.params.card)
                  : route.params.card
            };
          },
          beforeEnter: (to, from, next) => {
            routerUtils.isAllowedGuard(
              policyList.modifyCreditCards,
              to,
              from,
              next
            );
          }
        }
      ]
    },
    {
      path: "/weedmaps/callback",
      name: "weedmaps-callback",
      redirect: `/application-management/location-api/weedmaps-integration`
    },
    {
      path: "/forbidden",
      name: "forbidden",
      component: () =>
        import(/* webpackChunkName: "forbidden" */ "@/components/Forbidden/Forbidden.component")
    },
    {
      path: "*",
      name: "not-found",
      component: () =>
        import(/*webpackChunkName: "not-found" */ "@/components/NotFound/NotFound.component")
    }
  ]
});

router.beforeEach(routerUtils.checkIfAuthenticated);
